import { useTranslation } from "react-i18next";

import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { z } from "zod";
import { DEFAULT_ASSET_CLASS_OPTIONS } from "./form-conf";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";

const filterA5StaticDatumsSchema = z.object({
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  code_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  ticker_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_contains: z.string().optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
});

export type FilterA5StaticDatumsSchema = z.infer<
  typeof filterA5StaticDatumsSchema
>;

const assetDepFields: readonly DepField<FilterA5StaticDatumsSchema>[] = [
  {
    name: "name_contains",
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  { name: "isin_contains", key: "isin", extract: "text", bucket: "equals" },
  { name: "code_contains", key: "code", extract: "text", bucket: "equals" },
  {
    name: "ticker_contains",
    key: "ticker",
    extract: "text",
    bucket: "contains",
  },
];

const TABLE_NAME = "a5_static_data";

export function FilterA5StaticDatums() {
  const { t, i18n } = useTranslation("tables");

  const fields: Field[] = [
    {
      name: "name_contains",
      label: t(`${TABLE_NAME}.searchbox.name_contains`, { defaultValue: "Name" }),
      type: "asyncAutocomplete",
      base_url: "a5/a5_static_data",
      column_name: "name",
      filters: makeFiltersBuilder<FilterA5StaticDatumsSchema>(
        assetDepFields,
        "name_contains",
      ),
    },
    {
      name: "isin_contains",
      label: t(`${TABLE_NAME}.searchbox.isin_contains`, { defaultValue: "ISIN" }),
      type: "asyncAutocomplete",
      base_url: "a5/a5_static_data",
      column_name: "isin",
      filters: makeFiltersBuilder<FilterA5StaticDatumsSchema>(
        assetDepFields,
        "isin_contains",
      ),
    },
    {
      name: "code_contains",
      label: t(`${TABLE_NAME}.searchbox.code_contains`, { defaultValue: "Code" }),
      type: "asyncAutocomplete",
      base_url: "a5/a5_static_data",
      column_name: "code",
      filters: makeFiltersBuilder<FilterA5StaticDatumsSchema>(
        assetDepFields,
        "code_contains",
      ),
    },
    {
      name: "ticker_contains",
      label: t(`${TABLE_NAME}.searchbox.ticker_contains`, { defaultValue: "Ticker" }),
      type: "asyncAutocomplete",
      base_url: "a5/a5_static_data",
      column_name: "ticker",
      filters: makeFiltersBuilder(assetDepFields, "ticker_contains"),
    },
    {
      name: "currency_contains",
      label: t(`${TABLE_NAME}.searchbox.currency_contains`, { defaultValue: "Currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: "asset_class_1_equals",
      assetSubclassFieldName: "asset_class_2_equals",
      assetClassLabel: t(`${TABLE_NAME}.searchbox.asset_class_1_equals`, { defaultValue: "Asset class" }),
      assetSubclassLabel: t(`${TABLE_NAME}.searchbox.asset_class_2_equals`, { defaultValue: "Sub asset class" }),
      client: "a5",
      staticDataTableName: "a5_static_data",
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetSubclassOptions: DEFAULT_ASSET_CLASS_OPTIONS,
    }),
  ];

  return (
    <FilterTable
      schema={filterA5StaticDatumsSchema}
      client="a5"
      tableName="a5_static_data"
      fields={fields}
    />
  );
}
